/**
 * External Entity · Relation Ontology — L1/L2 enum SSOT
 *
 * @see docs/product/EXTERNAL_ENTITY_ONTOLOGY.md
 * @see docs/product/ENTITY_RELATION_ONTOLOGY.md
 */

import type { CustomField, DomainReferences, UniversalBaseEntity } from './entity';

// ---------------------------------------------------------------------------
// L1 — ExternalEntityType (존재 형태)
// ---------------------------------------------------------------------------

export const EXTERNAL_ENTITY_TYPES = [
  'person',
  'organization',
  'institution',
  'government',
  'ai_agent',
  'virtual_entity',
] as const;

export type ExternalEntityType = (typeof EXTERNAL_ENTITY_TYPES)[number];

export const EXTERNAL_ENTITY_TYPE_LABELS_KO: Record<ExternalEntityType, string> = {
  person: '자연인',
  organization: '법인·단체',
  institution: '공공·준공공 기관',
  government: '정부·규제 기관',
  ai_agent: '자율 에이전트',
  virtual_entity: '비물리 주체',
};

export function isExternalEntityType(value: unknown): value is ExternalEntityType {
  return typeof value === 'string' && (EXTERNAL_ENTITY_TYPES as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// L2 — ExternalBusinessRole (업무 역할, Relation-scoped)
// ---------------------------------------------------------------------------

export const EXTERNAL_BUSINESS_ROLES = [
  'customer',
  'supplier',
  'buyer',
  'contractor',
  'consultant',
  'regulator',
  'investor',
  'shareholder',
  'lender',
  'borrower',
  'service_provider',
  'competitor',
  'prospect',
  'applicant',
] as const;

/**
 * Removed from L2 enum v1.3 — channel partnership = `buyer` + relation facets.
 * @see docs/architecture/TENANT_PORTAL_ARCHITECTURE.md §3.1.2
 */
export const LEGACY_CHANNEL_EXTERNAL_BUSINESS_ROLES = [
  'partner',
  'reseller',
  'distributor',
  'franchisee',
  'affiliate',
] as const;

export type LegacyChannelExternalBusinessRole =
  (typeof LEGACY_CHANNEL_EXTERNAL_BUSINESS_ROLES)[number];

const LEGACY_CHANNEL_ROLE_SET = new Set<string>(LEGACY_CHANNEL_EXTERNAL_BUSINESS_ROLES);

export type ExternalBusinessRole = (typeof EXTERNAL_BUSINESS_ROLES)[number];

export const EXTERNAL_BUSINESS_ROLE_LABELS_KO: Record<ExternalBusinessRole, string> = {
  customer: '거래 고객 (B2B CRM·AR)',
  supplier: '공급사',
  /** PartnerChannelType bridge — B2B 채널 발주 파트너. 소매 고객(customer)과 구분. */
  buyer: '발주 파트너 (B2B 채널)',
  contractor: '용역·프로젝트 수행',
  consultant: '자문',
  regulator: '규제·감독',
  investor: '투자자',
  shareholder: '주주',
  lender: '대주',
  borrower: '차주',
  service_provider: '서비스 제공',
  competitor: '경쟁',
  prospect: '잠재 고객',
  applicant: '가입·지원 신청자',
};

export function isExternalBusinessRole(value: unknown): value is ExternalBusinessRole {
  return (
    typeof value === 'string' && (EXTERNAL_BUSINESS_ROLES as readonly string[]).includes(value)
  );
}

/** Persist/API legacy `roles[]` → current enum (channel L2 → buyer or drop redundant partner). */
export function normalizeExternalBusinessRoles(roles: readonly string[]): ExternalBusinessRole[] {
  const hasLegacy = roles.some((role) => LEGACY_CHANNEL_ROLE_SET.has(role));
  if (!hasLegacy) {
    return roles.filter(isExternalBusinessRole);
  }

  const withoutLegacy = roles.filter((role) => !LEGACY_CHANNEL_ROLE_SET.has(role));
  const normalized = withoutLegacy.filter(isExternalBusinessRole);

  const hadPartner = roles.includes('partner');
  const hadResellerLike = roles.some(
    (role) =>
      role === 'reseller' || role === 'distributor' || role === 'franchisee' || role === 'affiliate'
  );
  const hadBuyer = roles.includes('buyer') || normalized.includes('buyer');

  // Legacy join: partner + supplier (no buyer channel) — drop partner only.
  if (hadPartner && normalized.includes('supplier') && !hadResellerLike && !hadBuyer) {
    return [...new Set(normalized)];
  }

  const needsBuyer = hadResellerLike || hadBuyer || hadPartner || normalized.length === 0;

  if (needsBuyer && !normalized.includes('buyer')) {
    normalized.push('buyer');
  }

  return [...new Set(normalized)];
}

/** API·nav legacy role string → current enum (channel L2 → buyer). */
export function coerceExternalBusinessRole(
  value: string | null | undefined
): ExternalBusinessRole | null {
  if (value == null || value === '') return null;
  if (isExternalBusinessRole(value)) return value;
  return normalizeExternalBusinessRoles([value])[0] ?? null;
}

// ---------------------------------------------------------------------------
// L2 facet — RelationshipNature
// ---------------------------------------------------------------------------

export const RELATIONSHIP_NATURES = [
  'commercial',
  'operational',
  'financial',
  'legal',
  'strategic',
  'knowledge',
  'social',
  'regulatory',
] as const;

export type RelationshipNature = (typeof RELATIONSHIP_NATURES)[number];

export const RELATIONSHIP_NATURE_LABELS_KO: Record<RelationshipNature, string> = {
  commercial: '매매·거래',
  operational: '운영·실행 협력',
  financial: '자금·신용',
  legal: '계약·법무',
  strategic: '전략 제휴',
  knowledge: '지식·정보 교환',
  social: '커뮤니티·네트워크',
  regulatory: '규제·준수',
};

export function isRelationshipNature(value: unknown): value is RelationshipNature {
  return typeof value === 'string' && (RELATIONSHIP_NATURES as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// L2 bridge — ExternalArchetype (Flow · Insight 내부 참조)
// ---------------------------------------------------------------------------

export const EXTERNAL_ARCHETYPES = [
  'buyer',
  'seller',
  'producer',
  'consumer',
  'operator',
  'service_provider',
  'knowledge_provider',
  'capital_provider',
  'capital_receiver',
  'regulator',
  'influencer',
  'observer',
] as const;

export type ExternalArchetype = (typeof EXTERNAL_ARCHETYPES)[number];

export const EXTERNAL_ARCHETYPE_LABELS_KO: Record<ExternalArchetype, string> = {
  buyer: '구매자',
  seller: '판매자',
  producer: '생산자',
  consumer: '소비자',
  operator: '운영·유통 주체',
  service_provider: '서비스 제공',
  knowledge_provider: '지식 제공',
  capital_provider: '자본 공급',
  capital_receiver: '자본 수요',
  regulator: '규제',
  influencer: '영향력 주체',
  observer: '관찰·감사',
};

export function isExternalArchetype(value: unknown): value is ExternalArchetype {
  return typeof value === 'string' && (EXTERNAL_ARCHETYPES as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// L2 — TenantExternalRelation (목표 persist 모델)
// ---------------------------------------------------------------------------

export const TENANT_EXTERNAL_RELATION_STATUSES = [
  'active',
  'inactive',
  'suspended',
  'terminated',
] as const;

export type TenantExternalRelationStatus = (typeof TENANT_EXTERNAL_RELATION_STATUSES)[number];

export const TENANT_EXTERNAL_RELATION_STATUS_LABELS_KO: Record<
  TenantExternalRelationStatus,
  string
> = {
  active: '활성',
  inactive: '비활성',
  suspended: '일시 중지',
  terminated: '종료',
};

export function isTenantExternalRelationStatus(
  value: unknown
): value is TenantExternalRelationStatus {
  return (
    typeof value === 'string' &&
    (TENANT_EXTERNAL_RELATION_STATUSES as readonly string[]).includes(value)
  );
}

/** Relation-scoped capability delta — grants add · revokes subtract after catalog merge (TER1). */
export interface TenantExternalRelationCapabilityOverrides {
  grants?: string[];
  revokes?: string[];
}

export interface TenantExternalRelation {
  id: string;
  tenantId: string;
  externalEntityId: string;

  roles: ExternalBusinessRole[];
  customRoles?: string[];
  natures?: RelationshipNature[];
  archetypes?: ExternalArchetype[];
  /**
   * @deprecated flat list — migrate to `capabilityOverrides.grants` (portal_access compat).
   * Resolver treats legacy entries as grants during TER1 transition.
   */
  capabilities?: string[];
  capabilityOverrides?: TenantExternalRelationCapabilityOverrides;

  status: TenantExternalRelationStatus;
  partnerTierId?: string;
  virtualSpaceIds?: string[];

  /** F-tier 대행: 고객 tenant 격리 */
  contextTenantId?: string;

  createdAt: Date;
  updatedAt: Date;
}

// ---------------------------------------------------------------------------
// L1 — ExternalEntityRecord (UniversalBaseEntity + type)
// ---------------------------------------------------------------------------

export interface ExternalEntityIdentifiers {
  taxId?: string;
  domain?: string;
  agentId?: string;
}

/**
 * External entity record — L0 shell + L1 `type`.
 * Roles · partnerTier · relationshipStatus belong on TenantExternalRelation (L2).
 */
export interface ExternalEntityRecord extends UniversalBaseEntity {
  type: ExternalEntityType;
  identifiers?: ExternalEntityIdentifiers;
  customFields?: CustomField[];
  domainReferences?: DomainReferences;
}

export function isExternalEntityRecord(obj: unknown): obj is ExternalEntityRecord {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'id' in obj &&
    'name' in obj &&
    'tenantId' in obj &&
    'type' in obj &&
    isExternalEntityType((obj as ExternalEntityRecord).type)
  );
}
